<template>
  <div class="max-w-full mx-auto px-6 py-6 dark:bg-gray-600 dark:border-gray-600">
    <div class="bg-gray-100 dark:bg-gray-800 relative shadow-md sm:rounded-lg overflow-visible">
      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400 border-separate border-spacing-y-3">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr class="relative overflow-hidden">
              <th scope="col" class="px-4 py-3 relative">
                <button @mouseenter="showLegend = true" @mouseleave="showLegend = false"
                  class="flex items-center space-x-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1 1-7.07 17.07A10 10 0 0 1 12 2z" />
                  </svg>
                  <span class="sr-only">Legend</span>
                </button>

                <!-- Legend Popover -->
                <div v-if="showLegend"
                  class="absolute z-50 mt-2 w-64 p-4 bg-white dark:bg-gray-700 rounded-md shadow-lg border border-gray-200 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-100">
                  <h4 class="text-md font-semibold mb-2">Ticket Urgency Colors</h4>
                  <ul class="space-y-1">
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-gray-500"></span>
                      Closed
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-4 h-3 mr-2 rounded-full bg-red-500"></span>
                      Deadline Past / Unacknowledged
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-blue-500"></span>
                      Due Before Today
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-orange-500"></span>
                      Due Today
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-yellow-500"></span>
                      Due within 24 hours
                    </li>
                    <li class="flex items-center">
                      <span class="inline-block w-3 h-3 mr-2 rounded-full bg-green-500"></span>
                      Future
                    </li>
                  </ul>
                </div>
              </th>
              <th scope="col" class="relative pl-6 pr-4 py-2">
                <!-- <div
                  :class="[
                    'absolute inset-y-0 left-0 w-6 bg-gray-50 dark:bg-gray-700'
                  ]"
                ></div> -->
                ID
              </th>
              <th scope="col" class="px-4 py-3">Actions</th>
              <th scope="col" class="px-4 py-3">Details</th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Submitted
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Priority
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Requested By
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Assigned To
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Last Activity
              </th>
              <th scope="col" class="px-4 py-3 cursor-pointer">Hours
              </th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-600 divide-y divide-gray-200 dark:divide-gray-700 rounded-l-xl border-r">
            <template v-if="ticket">
              <tr class="relative overflow-hidden" :class="{ 'expanded-parent-row': isDetailsVisible(ticket.id) }">
                <td class="relative pl-6 pr-4 py-2">
                  <div :class="[
                    'absolute inset-y-0 left-0 w-6 rounded-l-xl',
                    getUrgencyColorClass(ticket.urgencycolor)
                  ]"></div>
                </td>
                <td class="pr-4 py-2">
                  <div class="inline-flex items-center gap-2 align-middle">
                    <div :class="[
                      'inline-flex',
                      'items-center',
                      'rounded-md',
                      'px-2',
                      'dark:bg-opacity-40',
                      'py-1',
                      'text-xs',
                      'font-medium',
                      `text-${ticket.urgencycolor}-700`,
                      `bg-${ticket.urgencycolor}-50`,
                      `ring-${ticket.urgencycolor}-700/10`,
                      'ring-1',
                      'ring-inset',
                      `dark:bg-${ticket.urgencycolor}-50/20`,
                      `dark:ring-${ticket.urgencycolor}-400/75`,
                      `dark:text-${ticket.urgencycolor}-300`
                    ]">{{ ticket.id }}
                      <svg v-if="ticket.has_job_today_for_user" class="h-3 w-3 ml-1"
                        :class="ticket.is_job_running_now ? 'text-red-500' : 'text-green-500'" viewBox="0 0 8 8"
                        fill="currentColor">
                        <circle cx="4" cy="4" r="4" />
                      </svg>
                    </div>
                    <button @click="copyTicketId(ticket.id)"
                      class="text-gray-500 hover:text-yellow-500 dark:text-white focus:outline-none"
                      title="Copy Ticket ID">
                      <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" viewBox="0 0 24 24">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                  </div>
                  <button v-if="ticket.acknowledged == 0 && userId == ticket.tech_id" title="Acknowledge Ticket" :class="[
                    'inline-flex',
                    'mt-2',
                    'items-center',
                    'rounded-md',
                    'px-2',
                    'py-1',
                    'text-xs',
                    'font-medium',
                    `text-green-700`,
                    `bg-green-50`,
                    `ring-green-700/10`,
                    'ring-1',
                    'ring-inset',
                    `dark:bg-green-50/20`,
                    `dark:ring-green-400/75`,
                    `dark:text-green-300`
                  ]" @click="acknowledgeTicket(ticket.id)"> Acknowledge
                  </button>
                </td>
                <td class="px-4 py-2">
                  <div class="flex items-center justify-left space-x-2">
                    <button @click="startJob(ticket.id)" title="Start Job" v-if="!userroles.includes(80)">
                      <svg class="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polygon points="10 8 16 12 10 16 10 8" />
                      </svg>
                    </button>
                    <button v-if="ticket.completed && ticket.completed !== '0000-00-00 00:00:00'" title="Reopen Ticket"
                      @click="openReopenTicketModal(ticket)">
                      <svg class="h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none"
                        viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                      </svg>

                    </button>
                    <button v-else title="Mark as Complete" @click="openSetCompleteTicketModal(ticket)">
                      <svg class="h-5 w-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    </button>
                    <button title="Transfer" @click="openTransferTicketModal(ticket)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="5" cy="18" r="2" />
                        <circle cx="19" cy="6" r="2" />
                        <path d="M19 8v5a5 5 0 0 1 -5 5h-3l3 -3m0 6l-3 -3" />
                        <path d="M5 16v-5a5 5 0 0 1 5 -5h3l-3 -3m0 6l3 -3" />
                      </svg>
                    </button>
                    <button @click="editTicket(ticket.id)" title="Edit Ticket">
                      <svg class="h-5 w-5 text-yellow-500" width="24" height="24" viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2"
                        stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button class="flex items-center space-x-1 hover:text-blue-500" @click.stop="openEmailModal(ticket)"
                      title="Send Email">
                      <svg class="w-5 h-5 text-blue-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                        width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                          d="m3.5 5.5 7.893 6.036a1 1 0 0 0 1.214 0L20.5 5.5M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z" />
                      </svg>
                    </button>
                    <button @click="copyTicketUrl(ticket.id)" title="Copy Ticket URL"
                      class="text-blue-500 focus:outline-none">
                      <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" viewBox="0 0 24 24">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                    </button>
                    <div v-if="copiedTicketId === ticket.id"
                      class="absolute bottom-10 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md transition-opacity duration-300">
                      Copied!
                    </div>
                  </div>
                  <div class="flex justify-left space-x-2 mt-12">
                    <button @click="addJob(ticket.id)" title="Add Job" v-if="!userroles.includes(80)">
                      <svg class="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 14v6m-3-3h6M6 10h2a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2zm10 0h2a2 2 0 002-2V6a2 2 0 00-2-2h-2a2 2 0 00-2 2v2a2 2 0 002 2zM6 20h2a2 2 0 002-2v-2a2 2 0 00-2-2H6a2 2 0 00-2 2v2a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button @click="toggleQuickNoteForm(ticket.id)" title="Add Note">
                      <svg class="h-5 w-5 text-green-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <line x1="13" y1="20" x2="20" y2="13" />
                        <path d="M13 20v-6a1 1 0 0 1 1 -1h6v-7a2 2 0 0 0 -2 -2h-12a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="openMergeTicketModal(ticket)" title="Merge ticket"
                      :disabled="!isEditable" class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-cyan-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="7" cy="18" r="2" />
                        <circle cx="7" cy="6" r="2" />
                        <circle cx="17" cy="12" r="2" />
                        <line x1="7" y1="8" x2="7" y2="16" />
                        <path d="M7 8a4 4 0 0 0 4 4h4" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" title="Add Reminder" @click="openCreateReminderModal(ticket)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <circle cx="12" cy="13" r="7" />
                        <polyline points="12 10 12 13 14 13" />
                        <line x1="7" y1="4" x2="4.25" y2="6" />
                        <line x1="17" y1="4" x2="19.75" y2="6" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="deleteTicket(ticket.id)" title="Delete Ticket"
                      :disabled="!isEditable" class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <button v-if="!userroles.includes(80)" @click="openTicketInvoice(ticket.id)"
                      title="Open Ticket Invoice" :disabled="!isEditable"
                      class="disabled:opacity-50 disabled:cursor-not-allowed">
                      <svg class="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-4 py-2 font-medium text-gray-900 dark:text-white">
                  <div>{{ ticket.redmineissueid != 0 ? ticket.redmineissueid : '' }}</div>
                  <div class="markdown-body" v-html="renderMarkdown(ticket.description)"></div>
                  <div>
                    <div class="mb-2 p-4"><strong>AIM Instruction-Notes: </strong>
                      <div v-if="ticket.delegatornotes" v-html="renderMarkdown(ticket.delegatornotes)"></div>
                      <div v-else>None</div>
                    </div>
                    <div class="mb-4 p-4"><strong>Private Notes: </strong>
                      <div v-if="ticket.privateinfo" v-html="renderMarkdown(ticket.privateinfo)"></div>
                      <div v-else>None</div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-2 whitespace-nowrap w-16"><span>{{ ticket.submitted }} </span><br>
                  <div class="flex justify-center">
                    <span
                      class="inline-flex items-center justify-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 dark:bg-purple-50/20 dark:ring-purple-400/75 dark:text-purple-300">
                      {{ ticket.daysold ? ticket.daysold : 'N/A' }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <div class="flex items-center justify-left space-x-2" v-click-outside="closePriorityOptions">
                    <button title="Back a day" @click="adjustPriority(ticket, -1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(270 12 12)" />
                      </svg>
                    </button>
                    <div class="overflow-visible">
                      <button @click="togglePriorityOptions(ticket.id)" class="relative w-16">{{ ticket.pdate
                        }}</button>
                      <div v-if="showPriorityOptions === ticket.id"
                        class="absolute z-50 mt-1 bg-white shadow-lg w-48 rounded-md">
                        <ul class="text-gray-700 text-xxs">
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, 'today')">Today</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, 'tomorrow')">Tomorrow</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '2')">2
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '3')">3
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '4')">4
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '5')">5
                            Days</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer" @click="setPriorityDate(ticket, '6')">6
                            Days</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_week')">1 Week</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '2_weeks')">2 Weeks</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '3_weeks')">3 Weeks</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_month')">1 Month</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '2_months')">2 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '3_months')">3 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '6_months')">6 Months</li>
                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '9_months')">9 Months</li>

                          <li class="px-4 py-0 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityDate(ticket, '1_year')">1 Year</li>
                        </ul>
                      </div>
                    </div>
                    <button title="Add a Day" @click="adjustPriority(ticket, 1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" />
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(90 12 12)" />
                      </svg>
                    </button>
                  </div>
                  <div class="flex items-center justify-left space-x-2" v-click-outside="closePriorityTimeOptions">
                    <button title="Decrease Time" @click="adjustPriorityTime(ticket, -1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(270 12 12)" />
                      </svg>
                    </button>
                    <div class="overflow-visible">
                      <button @click="togglePriorityTimeOptions(ticket.id)" class="relative w-16">
                        {{ ticket.ptime }}
                      </button>
                      <div v-if="showPriorityTimeOptions === ticket.id"
                        class="absolute z-50 mt-1 bg-white shadow-lg w-48 rounded-md">
                        <ul class="text-gray-700">
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'now')">Now</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'next_hour')">Next Hour</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, '2_hours')">+2 Hours</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'morning')">Morning (9 AM)</li>
                          <li class="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            @click="setPriorityTime(ticket, 'evening')">Evening (6 PM)</li>
                        </ul>
                      </div>
                    </div>
                    <button title="Increase Time" @click="adjustPriorityTime(ticket, 1)">
                      <svg class="h-5 w-5 text-blue-500" width="24" height="24" viewBox="0 0 24 24" stroke-width="2"
                        stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M18 15l-6-6l-6 6h12" transform="rotate(90 12 12)" />
                      </svg>
                    </button>
                  </div>
                </td>
                <td class="px-4 py-2 align-top text-sm text-gray-700 dark:text-gray-200 w-[15%] relative">
                  <div class="flex flex-col space-y-1">
                    <!-- Client name (red if notes/warnings exist) -->
                    <div class="text-sm font-semibold" :class="hasClientNotesOrWarnings(ticket)
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-800 dark:text-gray-100'">
                      {{ ticket.clientName }}
                    </div>
                    <a :href="`https://atms.aimitservices.com/addressBook.php?search=${getRequesterName(ticket)}&fname=1&lname=1`"
                      class="text-blue-600 hover:underline" target="_blank">
                      {{ getRequesterName(ticket) }}
                    </a>
                    <a v-for="(ph, phi) in getRequesterPhones(ticket)" :key="phi"
                      :href="`tel:${ph.number}${ph.ext ? ',' + ph.ext : ''}`"
                      class="text-blue-600 hover:underline">
                      {{ formatPhone(ph.number) }}
                      <span v-if="ph.ext"> x{{ ph.ext }}</span>
                      <span v-if="ph.label"> ({{ ph.label }})</span>
                    </a>
                    <a :href="`mailto:${getRequesterEmail(ticket)}`" class="text-blue-600 hover:underline"
                      target="_blank">
                      {{ getRequesterEmail(ticket) }}
                    </a>

                    <div class="inline-flex items-center justify-center mt-0.5 px-1 py-0.5 text-xs font-semibold rounded cursor-pointer 
                            bg-gray-100 text-gray-700 border border-gray-400 hover:bg-gray-200
                            dark:bg-gray-900 dark:text-gray-100 dark:border-gray-500 w-fit"
                      @click="showTooltip = !showTooltip" v-click-outside="closeTooltip">
                      Delegator Info
                    </div>

                    <div v-if="showTooltip && (ticket.delegator_user || ticket.watcher.length > 0)" class="absolute z-10 w-64 p-2 mt-1 mb-4 text-sm bg-white border border-gray-300 rounded-md shadow-md
                            dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                      <p v-if="ticket.delegator_user">
                        <strong>Delegated By:</strong>
                        {{ ticket.delegator_user.firstname }} {{ ticket.delegator_user.lastname }}<br />
                        <strong>Email:</strong> {{ ticket.delegator_user.email }}
                      </p>
                      <div v-if="ticket.watcher.length">
                        <strong>Watchers:</strong>
                        <ul>
                          <li v-for="watch in ticket.watcher" :key="watch.id">
                            {{ watch.contact.name_first }} {{ watch.contact.name_last }}<br />
                            <strong>Email:</strong> {{ watch.contact.email }}
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div v-if="hasClientNotesOrWarnings(ticket)" class="mt-2 p-2 text-[11px] leading-snug bg-yellow-50 border border-yellow-300 rounded
                          text-gray-800 dark:bg-yellow-900 dark:border-yellow-600 dark:text-yellow-50">
                    <div v-if="ticket.client && ticket.client.warning && ticket.client.warning.trim() !== ''"
                      class="mb-1">
                      <div class="font-semibold text-red-700 dark:text-red-300">
                        Warning
                      </div>
                      <div class="whitespace-pre-wrap">
                        {{ ticket.client.warning }}
                      </div>
                    </div>

                    <div v-if="ticket.client && ticket.client.notes && ticket.client.notes.trim() !== ''">
                      <div class="font-semibold text-gray-800 dark:text-gray-100">
                        Client Notes
                      </div>
                      <div class="whitespace-pre-wrap">
                        {{ ticket.client.notes }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-4 py-2">{{ ticket.assigned_to }}</td>
                <td class="px-4 py-2 whitespace-nowrap">
                  <div class="flex flex-row space-x-1">
                    <span :class="[
                      'inline-flex',
                      'items-center',
                      'rounded-md',
                      'px-2',
                      'py-1',
                      'text-xs',
                      'font-medium',
                      `text-indigo-700`,
                      `bg-indigo-50`,
                      `ring-indigo-700/10`,
                      'ring-1',
                      'ring-inset',
                      `dark:bg-indigo-50/20`,
                      `dark:ring-indigo-400/75`,
                      `dark:text-indigo-300`
                    ]">{{ ticket.lastjobdate ? ticket.lastjobdate : 'N/A' }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-2">
                  <span
                    class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-50/20 dark:ring-blue-400/75 dark:text-blue-300">{{
                      ticket.logged_time ? ticket.logged_time:'N/A' }}</span>
                </td>
              </tr>
              <transition name="quicknote-transition">
                <tr v-if="isQuickNoteFormVisible(ticket.id)" :key="ticket.id + '-quicknote'"
                  class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="8" class="px-4 py-2">
                    <div class="p-4 rounded-md shadow-md bg-yellow-100 dark:bg-gray-900">
                      <div class="grid grid-cols-3 gap-4 mb-2">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Time</label>
                          <input type="number" v-model="quickNoteTime" step="0.05"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100"
                            min="0">
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">IA</label>
                          <input type="number" v-model="quickNoteIA" step="0.05"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100"
                            min="0">
                        </div>
                        <div>
                          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100 mr-2">Publish</label>
                          <input type="checkbox" v-model="quickNotePublish"
                            class="rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                        </div>
                      </div>
                      <div :ref="`quicknote_editor_${ticket.id}`"></div>
                      <div class="mt-4 flex justify-end space-x-2">
                        <button @click="saveQuickNote(ticket)"
                          class="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-400">Save</button>
                        <button @click="toggleQuickNoteForm(ticket.id)"
                          class="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-600 dark:bg-red-700 dark:hover:bg-red-500">Cancel</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </transition>
              <transition name="details-transition">
                <tr v-if="isDetailsVisible(ticket.id)" :key="ticket.id + '-details'"
                  class="bg-gray-100 dark:bg-gray-700">
                  <td colspan="12" class="px-4 py-2">

                    <div class="flex flex-col md:flex-row gap-6"
                      v-if="(ticket.notes && ticket.notes.length > 0) || (ticket.documents && ticket.documents.length > 0)">
                      <div v-if="(ticket.notes && ticket.notes.length > 0)" :class="[
                        'p-4 shadow-md bg-gray-50 dark:bg-gray-900 rounded-md overflow-visible max-h-none',
                        ticket.documents && ticket.documents.length ? 'md:w-4/5' : 'w-full'
                      ]">
                        <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Notes</h4>
                        <!-- Timeline for ticket notes -->
                        <ol class="relative border-l border-gray-200 dark:border-gray-700">
                          <li v-for="note in ticket.notes" :key="note.id" class="mb-10 ml-6">
                            <div v-if="note">
                              <span
                                class="absolute flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full -left-4 ring-4 ring-white dark:ring-gray-900 dark:bg-blue-900">
                                <svg aria-hidden="true" class="w-3 h-3 text-blue-800 dark:text-blue-300"
                                  fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z" />
                                </svg>
                              </span>
                              <div class="flex justify-between items-center">
                                <time class="mb-1 text-sm font-normal leading-none text-gray-400 dark:text-gray-500">{{
                                  note.lastedittime != '0000-00-00 00:00:00' ?note.lastedittime : note.ts }}</time>
                              </div>
                              <h3 class="text-lg font-semibold text-gray-900 dark:text-white" v-if="note.user_profile">
                                {{ note.user_profile.firstname }} {{ note.user_profile.lastname }}</h3>
                              <h3 class="text-lg font-semibold text-gray-900 dark:text-white"
                                v-if="note.contact && !note.user_profile">{{ note.contact.name_first }} {{
                                note.contact.name_last }}</h3>
                              <div v-if="!isNoteEditing(note.id)" class="w-full">
                                <div :id="`note-viewer-${note.id}`"></div>

                                <div class="mt-2 flex items-center gap-2">
                                  <span class="mb-4 text-xs text-gray-500 dark:text-gray-400">Total Time: {{
                                    note.totaltime }}</span>
                                  <button v-if="canEditNote(note)" @click="startEditNote(ticket, note)"
                                    class="bg-yellow-500 text-white px-3 py-1 rounded-md hover:bg-yellow-400">
                                    Edit
                                  </button>
                                </div>
                              </div>
                              <div v-else class="w-full">
                                <div class="grid grid-cols-3 gap-4 mt-3 mb-3">
                                  <div>
                                    <label
                                      class="block text-sm font-medium text-gray-700 dark:text-gray-100">Time</label>
                                    <div class="relative mt-1">
                                      <input type="text" v-model="editingNotes[note.id].time"
                                        class="block w-full pr-8 rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100" />
                                      <div
                                        class="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col space-y-0.5">
                                        <button type="button" @click="adjustTime(note.id, 'time', 0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▲
                                        </button>
                                        <button type="button" @click="adjustTime(note.id, 'time', -0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▼
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">IA</label>
                                    <div class="relative mt-1">
                                      <input type="text" v-model="editingNotes[note.id].ia"
                                        class="block w-full pr-8 rounded-md border-gray-300 shadow-sm dark:bg-gray-500 dark:border-gray-900 dark:text-gray-100" />
                                      <div
                                        class="absolute right-1 top-1/2 transform -translate-y-1/2 flex flex-col space-y-0.5">
                                        <button type="button" @click="adjustTime(note.id, 'ia', 0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▲
                                        </button>
                                        <button type="button" @click="adjustTime(note.id, 'ia', -0.05)"
                                          class="text-gray-500 hover:text-blue-500 dark:text-gray-300 dark:hover:text-blue-400">
                                          ▼
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="flex items-end">
                                    <label
                                      class="inline-flex items-center text-sm font-medium text-gray-700 dark:text-gray-100">
                                      <input type="checkbox" v-model="editingNotes[note.id].publish"
                                        class="rounded-md border-gray-300 shadow-sm dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 mr-2" />
                                      Publish
                                    </label>
                                  </div>
                                </div>
                                <div :ref="`note_editor_${note.id}`"></div>

                                <div class="mt-3 flex justify-end gap-2">
                                  <button @click="saveEditNote(ticket, note)"
                                    class="bg-yellow-500 text-white px-4 py-2 rounded-md hover:bg-yellow-400">
                                    Save
                                  </button>
                                  <button @click="cancelEditNote(note.id)"
                                    class="bg-gray-400 text-white px-4 py-2 rounded-md hover:bg-gray-600 dark:bg-red-700 dark:hover:bg-red-500">
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            </div>
                          </li>
                        </ol>
                      </div>
                      <div v-if="ticket.documents && ticket.documents.length > 0"
                        class="border border-blue-300 bg-blue-50 dark:border-gray-900 dark:bg-gray-800 rounded-md p-3 mb-4 mt-4">
                        <h4 class="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-4">Attachments</h4>
                        <ul>
                          <li v-for="(doc, index) in ticket.documents" :key="index"
                            class="text-sm text-blue-600 dark:text-gray-100 flex items-center justify-between py-1">
                            <a :href="doc.presigned_url" :download="doc.doc_name" target="_blank"
                              class="hover:underline">
                              {{ doc.doc_name }}
                            </a>
                            <span class="ml-4 text-gray-600 text-xs text-right dark:text-gray-200">
                              {{ formatDateTime(doc.ts) }} &nbsp;
                              {{ doc.mime }} &nbsp;
                              {{ doc.user_profile?.firstname }} {{ doc.user_profile?.lastname }}
                            </span>
                            <button type="button"
                              class="ml-3 text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                              title="Delete attachment" @click="openDeleteAttachmentModal(ticket.id, doc)">
                              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </td>
                </tr>
              </transition>
            </template>
          </tbody>
        </table>
        <SendEmailModal :isVisible="showEmailModal" :ticketId="selectedEmailTicketId"
          :requesterName="selectedRequesterName" :requesterEmail="selectedRequesterEmail" :ccEmails="ccEmails"
          :ticketShortDescription="selectedTicketShortDescription" @close="showEmailModal = false"
          @send="handleEmailSend" />
        <MergeTicketsModal :isVisible="mergeTicketModal" :sourceTicketId="selectedTicketId"
          @close="mergeTicketModal = false" @complete="handleMergeTickets" />
        <CreateReminderModal :isVisible="createReminderModal" :sourceTicketId="selectedTicketId" :techs="clientTechs"
          :currentTech="selectedTech" @close="createReminderModal = false" @complete="handleCreateReminder" />
        <TransferTicket :isVisible="transferTicketModal" :sourceTicketId="selectedTicketId" :techs="clientTechs"
          :currentTech="selectedTech" @close="transferTicketModal = false" @complete="handleTicketTransfer" />
        <SetCompleteTicketModal :isVisible="setCompleteTicketModal" :ticketObj="selectedTicket"
          :requesterName="selectedRequesterName" :requesterEmail="selectedRequesterEmail"
          :requesterPhone="selectedRequesterPhone"
          :emailnotification="selectedTicketNotification" :allTypes="ticketTypes"
          :currentTicketTypeId="selectedTicketTypeId" :lastActivity="selectedTicketLastActivity"
          :sourceTicketId="selectedTicketId" :techs="clientTechs" :currentTech="selectedTech"
          @close="setCompleteTicketModal = false" @complete="handleCloseTicket" />
        <ReopenTicketModal :isVisible="reopenTicketModal" :sourceTicketId="selectedTicketId"
          :lastActivity="selectedTicketLastActivity" @close="reopenTicketModal = false"
          @complete="handleReopenTicket" />
        <ConfirmationModal :isVisible="showDeleteAttachmentModal" field="attachment"
          :value="deleteAttachmentDoc?.doc_name || deleteAttachmentDoc?.name || ''" @confirm="confirmDeleteAttachment"
          @cancel="cancelDeleteAttachment" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import MergeTicketsModal from "@/views/modals/MergeTicketsModal.vue";
import CreateReminderModal from "@/views/modals/CreateReminderModal.vue";
import SendEmailModal from '@/views/modals/SendEmailModal.vue';
import { marked } from 'marked';
import SetCompleteTicketModal from '@/views/modals/SetCompleteTicketModal.vue';
import ReopenTicketModal from "@/views/modals/ReopenTicketModal.vue";
import TransferTicket from '@/views/modals/TransferTicketModal.vue';
import Viewer from '@toast-ui/editor/dist/toastui-editor-viewer';
import '@toast-ui/editor/dist/toastui-editor-viewer.css';
import '@toast-ui/editor/dist/theme/toastui-editor-dark.css';
import { useToast } from "vue-toastification";
import { useJwt } from '@vueuse/integrations/useJwt';
import ThemeService from '@/services/ThemeService';
import '@toast-ui/editor/dist/toastui-editor.css';
import { Editor } from '@toast-ui/editor';
import ConfirmationModal from "@/views/modals/ConfirmationModal.vue";
export default {
  components: {
    MergeTicketsModal,
    CreateReminderModal,
    SetCompleteTicketModal,
    TransferTicket,
    SendEmailModal,
    Viewer,
    ReopenTicketModal,
    ConfirmationModal
  },
  props: {
    ticketId: {
      type: String,
      required: true
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      filters: {
        ticket_id: this.ticketId
      },
      userId: localStorage.getItem('user_id'),
      ticket: null,
      expandedTicketId: this.ticketId,
      quickNoteFormVisible: {},
      quickNoteText: '',
      quickNoteTime: 0,
      showLegend: false,
      quickNoteIA: 0,
      showEmailModal: false,
      selectedEmailTicketId: null,
      selectedTicket: null,
      selectedRequesterName: '',
      selectedRequesterEmail: '',
      selectedRequesterPhone: '',
      ccEmails: [],
      quickNotePublish: false,
      createReminderModal: false,
      mergeTicketModal: false,
      selectedTicketId: 0,
      selectedTicketNotification: '',
      selectedTech: 0,
      allTech: [],
      ticketTypes: [],
      clientTechs: [],
      setCompleteTicketModal: false,
      showPriorityOptions: null,
      showPriorityTimeOptions: null,
      transferTicketModal: false,
      showTooltip: false,
      selectedTicketTypeId: 0,
      selectedTicketLastActivity: '',
      reopenTicketModal: false,
      userroles: [],
      copiedTicketId: null,
      viewers: {},
      editingNotes: {},
      noteViewers: {},
      quickNoteEditors: {},
      selectedTicketShortDescription: "",
      showDeleteAttachmentModal: false,
      deleteAttachmentTicketId: null,
      deleteAttachmentDoc: null,
      deletingAttachment: false
    };
  },
  beforeUnmount() {
    Object.values(this.viewers).forEach(viewer => {
      if (viewer) viewer.destroy();
    });
    Object.values(this.editingNotes).forEach(n => {
      if (n?.editor) {
        try { n.editor.destroy(); } catch (_) { }
      }
    });
    Object.values(this.quickNoteEditors).forEach(ed => { if (ed) { try { ed.destroy(); } catch (_) { } } });
  },
  mounted() {
    this.fetchTicketDetails();
  },
  computed: {
    isEditable() {
      return this.userroles.some(r => [10, 20].includes(r));
    },
    isdark() {
      return ThemeService.isDark.value;
    }
  },
  watch: {
    isdark(newVal) {
      console.log('heyyyyyy (dark mode change detected): ', this.isdark);
      this.$nextTick(() => {
        if (this.ticket?.notes?.length) {
          this.ticket.notes.forEach(note => {
            const content = note.description || '';
            const viewerContainerId = `note-viewer-${note.id}`;
            const container = document.getElementById(viewerContainerId);
            if (!container) {
              console.warn('Viewer container not found:', viewerContainerId);
              return;
            }
            if (this.viewers[viewerContainerId]) {
              this.viewers[viewerContainerId].destroy();
              delete this.viewers[viewerContainerId];
            }
            container.innerHTML = '';

            const newEl = document.createElement('div');
            newEl.id = viewerContainerId;
            container.parentNode.replaceChild(newEl, container);
            this.initViewer(viewerContainerId, content);
          });
        }

        const openNoteIds = Object.keys(this.editingNotes || {});
        if (openNoteIds.length) {
          openNoteIds.forEach(id => {
            const st = this.editingNotes[id];
            if (st?.editor) {
              this.editingNotes[id]['description'] = st.editor.getMarkdown();
              try { st.editor.destroy(); } catch (_) { }
              st.editor = null;
            }
            this.initNoteEditor(id);
          });
        }
        Object.keys(this.quickNoteFormVisible || {}).forEach(tid => {
          if (this.quickNoteFormVisible[tid]) {
            const ed = this.quickNoteEditors[tid];
            if (ed) {
              this.quickNoteText = ed.getMarkdown();
            }
            this.destroyQuickNoteEditor(tid);
            this.initQuickNoteEditor(tid);
          }
        });
      });
    }
  },
  methods: {
    async fetchTicketDetails() {
      this.$updateLoading(1);

      try {
        const params = {
          page: 1,
          itemsPerPage: 1,
          sort_column: 'priority',
          sort_order: 'DESC',
          ...this.filters
        };

        // First wave — role, ticket fetch, and techs are independent.
        const [roleResponse, response, techResponse] = await Promise.all([
          axios.get(`user/role/${this.userId}`),
          axios.post('ticket/getAll', params),
          axios.get(`user/techs`),
        ]);
        this.userroles = roleResponse.data.roles;
        // Internal standing comes from the token scopes (internal/extTech), not
        // the old "has no companies" heuristic — company-less contact accounts
        // must not count as internal. Fail closed if the token can't be decoded.
        let isInternalUser = false;
        try {
          const { payload } = useJwt(localStorage.getItem('token'));
          const scopes = payload.value?.scopes || [];
          isInternalUser = scopes.includes('internal') || scopes.includes('extTech');
        } catch (e) {
          isInternalUser = false;
        }

        if (response.data.data.data.length > 0) {
          const ticketData = response.data.data.data[0];

          // Check if external user is trying to access an internal ticket
          if (!isInternalUser && ticketData.internal === 1) {
            this.$updateLoading(-1);
            this.toast.error('You do not have permission to view this internal ticket.');
            this.$router.push('/viewTickets'); // Redirect to tickets list
            return;
          }

          // If access is allowed, set the ticket data
          this.ticket = ticketData;
          console.log('Ticket:', this.ticket);
          this.expandedTicketId = this.ticketId;
          this.selectedTech = this.ticket.tech_id;
          this.toggleDetails(this.ticket.id);
          this.getTicketTypes(this.ticket.client.id);
          this.selectedTicketTypeId = this.ticket.tickettype_id;
          this.$nextTick(() => {
            if (this.ticket?.notes?.length) {
              this.ticket.notes.forEach(note => {
                if (this.isNoteEditing(note.id)) return;
                const content = note.description || '';
                this.initViewer(`note-viewer-${note.id}`, content);
              });
            }
            const openNoteIds = Object.keys(this.editingNotes || {});
            if (openNoteIds.length) {
              openNoteIds.forEach(id => {
                const st = this.editingNotes[id];
                if (st?.editor) {
                  this.editingNotes[id]['description'] = st.editor.getMarkdown();
                  try { st.editor.destroy(); } catch (_) { }
                  st.editor = null;
                }
                this.initNoteEditor(id);
              });
            }
          });
        } else {
          console.error('Ticket not found');
          this.toast.error('Ticket not found or you do not have permission to view it.');
          this.$router.push('/viewTickets');
        }

        this.allTech = techResponse.data;

      } catch (error) {
        console.error('Error fetching ticket details:', error);
        this.toast.error('Error loading ticket');
      } finally {
        this.$updateLoading(-1);
      }
    },
    async saveQuickNote(ticket) {
      try {
        this.$updateLoading(1);
        const ed = this.quickNoteEditors[ticket.id];
        const description = ed ? ed.getMarkdown() : (this.quickNoteText || '');
        const response = await axios.post('job/add-note', {
          client_id: ticket.company_id,
          ticket_id: ticket.id,
          contact_id: ticket.contact_id,
          description: description,
          time: this.quickNoteTime,
          ia: this.quickNoteIA,
          publish: this.quickNotePublish ? 1 : 0
        });
        console.log(response);
        this.toggleQuickNoteForm(ticket.id);
        await this.fetchTicketDetails();
      } catch (error) {
        console.error('Error saving quick note:', error);
        this.toast.error('Error saving quick note');
      } finally {
        this.$updateLoading(-1);
      }
    },
    hasClientNotesOrWarnings(ticket) {
      const c = ticket.client;
      if (!c) return false;

      const hasWarning = c.warning && c.warning.trim() !== '';
      const hasNotes = c.notes && c.notes.trim() !== '';
      return hasWarning || hasNotes;
    },
    initQuickNoteEditor(ticketId) {
      this.$nextTick(() => {
        let el = this.$refs[`quicknote_editor_${ticketId}`];
        if (Array.isArray(el)) el = el[0];
        if (!el) return;

        try {
          const ed = new Editor({
            el,
            initialValue: this.quickNoteText || '',
            height: '300px',
            previewStyle: 'vertical',
            initialEditType: 'markdown',
            usageStatistics: false,
            theme: this.isdark ? 'dark' : 'default',
            toolbarItems: [
              ['heading', 'bold', 'italic', 'strike'],
              ['hr', 'quote'],
              ['ul', 'ol', 'task', 'indent', 'outdent'],
              ['table', 'link'],
              ['code', 'codeblock'],
              ['scrollSync'],
            ],
            hooks: {
              addImageBlobHook: () => false
            }
          });
          this.quickNoteEditors[ticketId] = ed;
        } catch (e) {
          console.error('QuickNote editor init failed for', ticketId, e);
        }
      });
    },

    openDeleteAttachmentModal(ticketId, doc) {
      this.deleteAttachmentTicketId = ticketId;
      this.deleteAttachmentDoc = doc;
      this.showDeleteAttachmentModal = true;
    },

    async confirmDeleteAttachment() {
      if (!this.deleteAttachmentTicketId || !this.deleteAttachmentDoc) return;

      const docId = this.deleteAttachmentDoc.id ?? this.deleteAttachmentDoc.document_id;
      if (!docId) {
        this.showDeleteAttachmentModal = false;
        return;
      }

      this.deletingAttachment = true;

      try {
        await axios.post("ticket/attachment/delete", {
          ticket_id: this.deleteAttachmentTicketId,
          document_id: docId,
        });

        if (this.ticket?.documents?.length) {
          this.ticket.documents = this.ticket.documents.filter(d => {
            const did = d.id ?? d.document_id;
            return String(did) !== String(docId);
          });
        }
      } catch (e) {
        console.error("Delete attachment failed:", e);
      } finally {
        this.deletingAttachment = false;
        this.showDeleteAttachmentModal = false;
        this.deleteAttachmentTicketId = null;
        this.deleteAttachmentDoc = null;
      }
    },

    cancelDeleteAttachment() {
      if (this.deletingAttachment) return;
      this.showDeleteAttachmentModal = false;
      this.deleteAttachmentTicketId = null;
      this.deleteAttachmentDoc = null;
    },

    destroyQuickNoteEditor(ticketId) {
      const ed = this.quickNoteEditors[ticketId];
      if (ed) {
        try { ed.destroy(); } catch (_) { }
        delete this.quickNoteEditors[ticketId];
      }
    },
    isNoteEditing(noteId) {
      return !!this.editingNotes[noteId];
    },

    startEditNote(ticket, note) {
      const viewerId = `note-viewer-${ticket.id}-${note.id}`;
      this.destroyViewer(viewerId);
      if (this.editingNotes[note.id]?.editor) {
        try { this.editingNotes[note.id].editor.destroy(); } catch (_) { }
      }

      this.editingNotes = {
        ...this.editingNotes,
        [note.id]: {
          ticketId: ticket.id,
          description: this.fixMarkdownIndentation(note.description || ''),
          time: parseFloat(note.totaltime || 0).toFixed(2),
          ia: parseFloat(note.ignoranceadjustment || 0).toFixed(2),
          publish: !!note.publish_to_portal,
          editor: null
        }
      };

      this.$nextTick(() => this.initNoteEditor(note.id));
    },
    closeTooltip() {
      this.showTooltip = false;
    },

    initNoteEditor(noteId) {
      let el = this.$refs['note_editor_' + noteId];
      if (Array.isArray(el)) el = el[0];
      if (!el) {
        console.error('Note editor container not found for', noteId);
        return;
      }
      const st = this.editingNotes[noteId];
      if (!st) return;

      const editor = new Editor({
        el,
        initialValue: st.description,
        height: '500px',
        previewStyle: 'vertical',
        initialEditType: 'markdown',
        usageStatistics: false,
        theme: this.isdark ? 'dark' : 'default',
        toolbarItems: [
          ['heading', 'bold', 'italic', 'strike'],
          ['hr', 'quote'],
          ['ul', 'ol', 'task', 'indent', 'outdent'],
          ['table', 'link'],
          ['code', 'codeblock'],
          ['scrollSync'],
        ],
        hooks: {
          addImageBlobHook: () => false
        }
      });

      this.editingNotes[noteId].editor = editor;
    },
    adjustTime(noteId, field, delta) {
      let val = parseFloat(this.editingNotes[noteId][field]) || 0;
      val += delta;
      this.editingNotes[noteId][field] = Math.max(0, Math.round(val * 100) / 100);
    },
    async saveEditNote(ticket, note) {
      const st = this.editingNotes[note.id];
      if (!st || !st.editor) return;

      try {
        this.$updateLoading(1);

        const payload = {
          description: st.editor.getMarkdown(),
          ia: parseFloat(st.ia || 0),
          total: parseFloat(st.time || 0),
          publish: st.publish ? 1 : 0,
          client_id: ticket.company_id || 0,
          ticket_id: ticket.id || 0,
        };

        await axios.post(`/job/notes/${note.id}`, payload);

        try { st.editor.destroy(); } catch (_) { }
        delete this.editingNotes[note.id];
        this.editingNotes = { ...this.editingNotes };
        await this.fetchTicketDetails();
      } catch (err) {
        console.error('Error saving note:', err);
        this.toast?.error?.('Failed to save note');
      } finally {
        this.$updateLoading(-1);
      }
    },

    cancelEditNote(noteId) {
      const st = this.editingNotes[noteId];
      if (st?.editor) {
        try { st.editor.destroy(); } catch (_) { }
      }
      delete this.editingNotes[noteId];
      this.editingNotes = { ...this.editingNotes }
      this.$nextTick(() => {
        if (this.ticket?.notes?.length) {
          this.ticket.notes.forEach(note => {
            if (this.isNoteEditing(note.id)) return;
            const content = note.description || '';
            this.initViewer(`note-viewer-${note.id}`, content);
          });
        }
      });
    },
    async handleEmailSend(payload) {
      this.$updateLoading(1);
      try {
        await axios.post('job/send-email', {
          job_id: payload.jobId ? payload.jobId : 0,
          ticket_id: payload.ticketId ? payload.ticketId : 0,
          subject: payload.subject,
          to: payload.to,
          cc: payload.cc,
          body: payload.body,
        });
        this.showEmailModal = false;
        this.toast.success("Email sent successfully!");
        await this.fetchTicketDetails();
      } catch (err) {
        console.error("Error sending email:", err);
        this.toast.error("Failed to send email.");
      } finally {
        this.$updateLoading(-1);
      }
    },
    async copyTicketId(id) {
      try {
        await navigator.clipboard.writeText(id.toString());
        this.copiedTicketId = id;
        setTimeout(() => (this.copiedTicketId = null), 1000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    },
    openEmailModal(ticket) {
      this.selectedEmailTicketId = ticket.id;
      this.selectedRequesterName = this.getRequesterName(ticket);
      this.selectedRequesterEmail = this.getRequesterEmail(ticket);
      const rawDesc = ticket.desc_short || "";
      const cleanedDesc = rawDesc
        .replace(/[\r\n]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      this.selectedTicketShortDescription = cleanedDesc;
      let ccList = [];
      if (ticket.watcher && Array.isArray(ticket.watcher)) {
        ccList = ticket.watcher
          .map(w => w.contact?.email)
          .filter(email => !!email);
      }

      this.ccEmails = ccList;
      this.showEmailModal = true;
    },

    getRequesterName(ticket) {
      if (ticket.contact) {
        return ticket.contact.name;
      } else if (ticket.requester) {
        return ticket.requester;
      }
      return '';
    },

    getRequesterEmail(ticket) {
      if (ticket.contact) {
        return ticket.contact.email;
      } else if (ticket.email) {
        return ticket.email;
      }
      return '';
    },
    // All of a requester's phone numbers (primary + secondary), falling back to the
    // ticket's alternate phone when the contact has none.
    getRequesterPhones(ticket) {
      const contact = ticket.contact ?? false;
      const phones = [];

      if (contact) {
        if (contact.phone_primary) {
          phones.push({ number: contact.phone_primary, label: 'Primary', ext: contact.phone_primary_ext || '' });
        }
        if (contact.phone_secondary) {
          phones.push({ number: contact.phone_secondary, label: 'Secondary', ext: contact.phone_secondary_ext || '' });
        }
      }

      if (!phones.length && ticket.alternatephone) {
        phones.push({ number: ticket.alternatephone, label: 'Alternate', ext: '' });
      }

      return phones;
    },
    getRequesterPhone(ticket) {
      return this.getRequesterPhones(ticket)[0] || { number: '', label: '', ext: '' };
    },
    formatPhone(phone) {
      if (!phone) return '';

      const raw = String(phone).trim();
      let digits = raw.replace(/\D/g, '');

      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
      }
      if (digits.length === 10) {
        const area = digits.slice(0, 3);
        const prefix = digits.slice(3, 6);
        const line = digits.slice(6);
        return `(${area}) ${prefix}-${line}`;
      }
      return raw;
    },
    getUrgencyColorClass(color) {
      const map = {
        red: 'bg-red-500',
        green: 'bg-green-500',
        yellow: 'bg-yellow-500',
        blue: 'bg-blue-500',
        purple: 'bg-purple-500',
        orange: 'bg-orange-500',
        gray: 'bg-gray-500'
        // Add all used urgency colors
      };
      return map[color] || 'bg-gray-400';
    },
    copyTicketUrl(ticketId) {
      const ticketUrl = `https://atms.aimit.io/ticket/${ticketId}`;
      navigator.clipboard.writeText(ticketUrl)
        .then(() => {
          //
          this.copiedTicketId = ticketId;
          setTimeout(() => {
            this.copiedTicketId = null; // Remove notification after 2 seconds
          }, 2000);
        })
        .catch(err => {
          console.error("Error copying ticket URL:", err);
        });
    },
    handleMergeTickets({ sourceTicketId, targetTicketId, reason }) {
      this.$updateLoading(1);
      axios.post('ticket/merge', { sourceTicketId: sourceTicketId, targetTicketId: targetTicketId, reason: reason })
        .then(response => {
          this.fetchTicketDetails(); // Refresh the tickets list
        })
        .catch(error => {
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    handleCreateReminder({ ticketId, techId, day, time, sendText, description }) {
      this.$updateLoading(1);
      axios.post('ticket/create-reminder', {
        ticketId: ticketId,
        techId: techId,
        day: day,
        time: time,
        sendText: sendText ? 1 : 0,
        description: description,
        contact_id: this.ticket.contact_id ?? 0,
        tickettype_id: this.ticket.tickettype_id ?? 0,
        company_id: this.ticket.company_id ?? 0
      })
        .then(response => {
          this.fetchTicketDetails(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error creating reminder:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    handleTicketTransfer(payload = {}) {
      const { ticketId, curTechId, newTechId, sendEmail, transferNotes } = payload;
      if (!ticketId) {
        console.warn('handleTicketTransfer called without valid payload:', payload);
        return;
      }
      this.$updateLoading(1);
      axios.post('ticket/transfer', {
        ticketId: ticketId,
        curTechId: curTechId,
        newTechId: newTechId,
        sendEmail: sendEmail,
        transferNotes: transferNotes
      })
        .then(response => {
          this.fetchTicketDetails(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error transferring ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    handleCloseTicket({ ticketId, techId, day, sendEmail, closingNote, ticketTypeId, aiEdited, notifyCompany }) {
      this.$updateLoading(1);
      axios.post('ticket/close', {
        ticketId: ticketId,
        techId: techId,
        day: day,
        sendEmail: sendEmail,
        closingNote: closingNote,
        ticketTypeId: ticketTypeId,
        aiEdited: aiEdited,
        notifyCompany: notifyCompany
      })
        .then(response => {
          this.fetchTicketDetails(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error closing ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    openReopenTicketModal(ticket) {
      this.selectedTicketId = ticket.id;
      this.selectedTicketLastActivity = new Date();
      this.reopenTicketModal = true;
    },
    async handleReopenTicket({ ticketId, day, note }) {
      this.$updateLoading(1);
      try {
        await axios.post('ticket/reopen', {
          ticketId,
          day,
          note
        });
        this.toast.success('Ticket reopened successfully!');
        this.fetchTicketDetails();
      } catch (error) {
        console.error('Error reopening ticket:', error);
        this.toast.error(error?.response?.data?.message || 'Failed to reopen ticket');
      } finally {
        this.$updateLoading(-1);
        this.reopenTicketModal = false;
      }
    },
    acknowledgeTicket(id) {
      this.$updateLoading(1);
      axios.post('ticket/acknowledge', {
        ticketId: id,
        ack: 1
      })
        .then(response => {
          this.fetchTicketDetails(); // Refresh the tickets list
        })
        .catch(error => {
          console.error('Error acknowledging ticket:', error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    formatDateTime(datetime) {
      const date = new Date(datetime);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    getTicketTypes(company_id) {
      this.$updateLoading(1);
      axios.get(`ticket/get-types/${company_id}`)
        .then((response) => {
          this.ticketTypes = response.data;
        })
        .catch((error) => {
          console.error("There was an error fetching ticket types:", error);
        }).finally(() => {
          this.$updateLoading(-1);
        });
    },
    toggleDetails(ticketId) {
      this.expandedTicketId = this.expandedTicketId === ticketId ? null : ticketId;
    },
    isDetailsVisible(ticketId) {
      return this.expandedTicketId === ticketId;
    },
    canEditNote(note) {
      const noteUserId = Number(note.user_id ?? 0);
      const userId = Number(this.userId ?? 0);
      return noteUserId > 0 && userId > 0 && noteUserId === userId;
    },
    startJob(ticketId) {
      // window.open(`https://atms.aimitservices.com/jobList.php?func=quick_add_job_from_ticket&ticket_id=${ticketId}`, '_blank');
      this.$updateLoading(1);
      if (this.ticket.acknowledged == 0 && this.ticket.tech_id == this.userId) {
        this.acknowledgeTicket(ticketId);
      }
      axios.post('job/quickjob', { ticketId })
        .then(response => {
          // window.open('/jobs', '_blank');
          this.toast.success(response.data.message);
          this.fetchTicketDetails();
        })
        .catch(error => {
          console.error("Failed to create quick job:", error);
        })
        .finally(() => {
          this.$updateLoading(-1);
        });
    },
    addJob(ticketId) {
      if (this.ticket.acknowledged == 0 && this.ticket.tech_id == this.userId) {
        this.acknowledgeTicket(ticketId);
      }
      this.$router.push({ name: 'addEditJob', query: { ticketId: ticketId } });
      // window.open(`https://atms.aimitservices.com/addEdit.php?ticketID=${ticketId}&return_to=tickets-summary-IMPROVED.php`, '_blank');
    },
    openTicketInvoice(ticketId) {
      let clientId = '';
      if (this.ticket.company_id != 0) {
        clientId = this.ticket.company_id;
      }
      window.open(`https://atms.aimitservices.com/administration.php?doSearch=true&jobStatus=all&companyID=${clientId}&search=${ticketId}&summarySelection=none`, '_blank');
    },
    toggleQuickNoteForm(ticketId) {
      const willShow = !this.quickNoteFormVisible[ticketId];
      if (willShow) {
        this.quickNoteText = '';
        this.quickNoteTime = 0;
        this.quickNoteIA = 0;
        this.quickNotePublish = false;
      } else {
        this.destroyQuickNoteEditor(ticketId);
      }
      this.quickNoteFormVisible = {
        ...this.quickNoteFormVisible,
        [ticketId]: willShow
      };

      if (willShow) {
        this.destroyQuickNoteEditor(ticketId);
        this.$nextTick(() => this.initQuickNoteEditor(ticketId));
      }
    },
    isQuickNoteFormVisible(ticketId) {
      return this.quickNoteFormVisible[ticketId] || false;
    },
    openMergeTicketModal(ticket) {
      this.selectedTicketId = ticket.id;
      this.mergeTicketModal = true;
    },
    async openCreateReminderModal(ticket) {
      this.selectedTicketId = ticket.id;
      try {
        this.$updateLoading(1);
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data;

        this.createReminderModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async openSetCompleteTicketModal(ticket) {
      this.selectedTicketId = ticket.id;
      this.selectedTicketTypeId = ticket.tickettype_id;
      this.selectedTicketLastActivity = ticket.last_activity;
      this.selectedTech = ticket.tech_id;
      this.selectedTicketNotification = ticket.emailnotification;
      this.selectedRequesterName = this.getRequesterName(ticket);
      this.selectedRequesterEmail = this.getRequesterEmail(ticket);
      this.selectedRequesterPhone = this.getRequesterPhone(ticket).number || '';
      this.selectedTicket = ticket;
      try {
        this.$updateLoading(1);
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data;

        this.setCompleteTicketModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    async openTransferTicketModal(ticket) {
      this.selectedTicketId = ticket.id;
      try {
        this.$updateLoading(1);
        // Await the API call to fetch active users (client techs)
        const response = await axios.get(`${axios.defaults.baseURL}user/active/transfer/${ticket.company_id}`);
        this.clientTechs = response.data; // Store the client techs in a variable
        // Open the modal only after fetching the techs
        this.transferTicketModal = true;
      } catch (error) {
        console.error("Error fetching active users for the client:", error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    editTicket(id) {
      this.$router.push({ name: 'editTicket', params: { id: id } });
    },
    renderMarkdown(text) {
      const html = marked.parse(this.preprocessMarkdown(text));
      // console.log(html);
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      doc.querySelectorAll('h2').forEach(el => {
        el.classList.add('mb-2', 'text-lg', 'font-semibold', 'text-gray-900', 'dark:text-white');
      });
      doc.querySelectorAll('h3, p').forEach(el => {
        el.classList.add('mb-2', 'text-base', 'font-semibold', 'text-gray-900', 'dark:text-white');
      });

      // Add classes to unordered lists and list items
      // doc.querySelectorAll('ul').forEach(ul => {
      //   ul.classList.add('max-w-md', 'space-y-1', 'text-gray-500', 'list-disc', 'list-inside', 'dark:text-gray-400');
      //   ul.querySelectorAll('li').forEach(li => {
      //     li.classList.add('list-item-class'); // add if specific LI classes are needed
      //   });
      // });

      // Style for all unordered lists
      doc.querySelectorAll('ul').forEach(ul => {
        ul.classList.add('max-w-md', 'space-y-1', 'text-gray-500', 'list-disc', 'list-inside', 'dark:text-gray-400');
      });

      // Additional style for nested lists
      doc.querySelectorAll('ul ul').forEach(nestedUl => {
        nestedUl.classList.add('ml-4', 'border-l', 'pl-4', 'mt-2', 'border-gray-200', 'dark:border-gray-700');
      });

      // Return the modified HTML as a string
      return doc.body.innerHTML;
    },
    preprocessMarkdown(markdownText) {
      // Split the Markdown text into lines
      const lines = markdownText.split('\n');
      const processedLines = lines.map(line => {
        // Check if the line starts with spaces followed by a dash (list item)
        // and replace leading spaces with a tab
        return line.replace(/^(\s+)-/, '\t-');
      });
      // Join the processed lines back into a single string
      return processedLines.join('\n');
    },
    closePriorityOptions() {
      this.showPriorityOptions = null;
    },
    closePriorityTimeOptions() {
      this.showPriorityTimeOptions = null;
    },
    togglePriorityTimeOptions(ticketId) {
      this.showPriorityTimeOptions = this.showPriorityTimeOptions === ticketId ? null : ticketId;
    },
    async setPriorityTime(ticket, timeOption) {
      const newTime = this.calculateNewTime(timeOption);
      await this.updateTicketPriorityTime(ticket.id, newTime);
      this.showPriorityTimeOptions = null; // Close the dropdown
    },
    async adjustPriorityTime(ticket, adjustment) {
      const adjustedTime = this.adjustTimeByHours(ticket.ptime, adjustment);
      await this.updateTicketPriorityTime(ticket.id, adjustedTime);
    },
    async updateTicketPriorityTime(ticketId, newTime) {
      this.$updateLoading(1);
      try {
        // Combine the existing date with the updated time
        const newDate = `${this.ticket.pdate}T${newTime}`;
        const response = await axios.post('ticket/update-priority', { ticketId, newDate });
        this.ticket.ptime = newTime;
      } catch (error) {
        console.error('Error updating priority time:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    calculateNewTime(timeOption) {
      const now = new Date();
      let newTime;
      switch (timeOption) {
        case 'now':
          newTime = now.toTimeString().slice(0, 5);
          break;
        case 'next_hour':
          newTime = new Date(now.setHours(now.getHours() + 1)).toTimeString().slice(0, 5);
          break;
        case '2_hours':
          newTime = new Date(now.setHours(now.getHours() + 2)).toTimeString().slice(0, 5);
          break;
        case 'morning':
          newTime = '09:00';
          break;
        case 'evening':
          newTime = '18:00';
          break;
        default:
          newTime = now.toTimeString().slice(0, 5);
      }
      return newTime;
    },
    adjustTimeByHours(currentTime, hours) {
      const [currentHour, currentMinute] = currentTime.split(':').map(Number);
      const newHour = (currentHour + hours + 24) % 24;
      return `${String(newHour).padStart(2, '0')}:${String(currentMinute).padStart(2, '0')}`;
    },
    togglePriorityOptions(ticketId) {
      this.showPriorityOptions = this.showPriorityOptions === ticketId ? null : ticketId;
    },
    async setPriorityDate(ticket, dayOption) {
      // Assuming you have a method to calculate the date based on the option
      const newDate = this.calculateNewDate(dayOption, ticket.ptime);
      await this.updateTicketPriorityDate(ticket.id, newDate);
      this.showPriorityOptions = null; // Close the dropdown
    },
    async adjustPriority(ticket, adjustment) {
      const adjustedDate = this.adjustDateByDays(ticket.pdate, adjustment, ticket.ptime);
      await this.updateTicketPriorityDate(ticket.id, adjustedDate);
    },
    async updateTicketPriorityDate(ticketId, newDate) {
      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/update-priority', { ticketId, newDate });
        let [datePart, timePart] = newDate.split('T');
        const shortYearDate = `${datePart.slice(2)}`;
        this.ticket.pdate = shortYearDate;
      } catch (error) {
        console.error('Error updating priority date:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    addMonths(date, n) {
      const d = new Date(date.getTime());
      const day = d.getDate();
      d.setMonth(d.getMonth() + n);
      if (d.getDate() < day) {
        d.setDate(0);
      }
      return d;
    },
    calculateNewDate(dayOption, ptime) {
      const today = new Date();
      let newDate;
      switch (dayOption) {
        case 'today':
          newDate = today;
          break;
        case 'tomorrow':
          newDate = new Date(today.setDate(today.getDate() + 1));
          break;
        case '2':
          newDate = new Date(today.setDate(today.getDate() + 2));
          break;
        case '3':
          newDate = new Date(today.setDate(today.getDate() + 3));
          break;
        case '4':
          newDate = new Date(today.setDate(today.getDate() + 4));
          break;
        case '5':
          newDate = new Date(today.setDate(today.getDate() + 5));
          break;
        case '6':
          newDate = new Date(today.setDate(today.getDate() + 6));
          break;
        case 'next_week':
        case '1_week':
          newDate = new Date(today.setDate(today.getDate() + 7));
          break;
        case 'two_week':
        case '2_weeks':
          newDate = new Date(today.setDate(today.getDate() + 14));
          break;
        case '3_weeks':
          newDate = new Date(today.setDate(today.getDate() + 21));
          break;
        // months
        case '1_month':
          newDate = this.addMonths(today, 1);
          break;
        case '2_months':
          newDate = this.addMonths(today, 2);
          break;
        case '3_months':
          newDate = this.addMonths(today, 3);
          break;
        case '6_months':
          newDate = this.addMonths(today, 6);
          break;
        case '9_months':
          newDate = this.addMonths(today, 9);
          break;
        // year
        case '1_year':
          newDate = this.addMonths(today, 12);
          break;
        default:
          newDate = today;
      }
      const localDate = newDate.toLocaleDateString('en-CA');
      return `${localDate}T${ptime}`;
    },
    destroyViewer(viewerId) {
      const v = this.viewers[viewerId];
      if (v) {
        try { v.destroy(); } catch (_) { }
        delete this.viewers[viewerId];
      }
    },
    initViewer(viewerId, content) {
      this.destroyViewer(viewerId);
      this.$nextTick(() => {
        const el = document.getElementById(viewerId);
        if (!el) {
          return;
        }
        try {
          this.viewers[viewerId] = new Viewer({
            el,
            initialValue: this.fixMarkdownIndentation(content || ''),
            theme: this.isdark ? 'dark' : 'default',
          });
        } catch (e) {
          console.error('Viewer init failed for', viewerId, e);
        }
      });
    },
    fixMarkdownIndentation(text) {
      return text
        .split('\n')
        .map(line => {
          // Fix indentation for list items that start with ' - '
          if (line.startsWith(' - ')) {
            line = '    - ' + line.slice(3);
          }

          // Fix markdown headings without space, e.g., '###Heading' → '### Heading'
          line = line.replace(/^(#{1,6})([^\s#])/g, '$1 $2');

          return line;
        })
        .join('\n');
    },
    adjustDateByDays(currentDate, days, ptime) {
      const parts = currentDate.split('-'); // 'YY-MM-DD'
      if (parts.length !== 3) return null;

      const [yy, mm, dd] = parts;
      // If you might sometimes get 4-digit years, handle both:
      const year = (yy.length === 2) ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
      const month = parseInt(mm, 10) - 1; // 0-based
      const day = parseInt(dd, 10);

      // Use local-time constructor to avoid UTC parsing issues
      const date = new Date(year, month, day);
      date.setDate(date.getDate() + Number(days || 0));

      // Format YYYY-MM-DD
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');

      return `${y}-${m}-${d}T${ptime}`;
    },
    async deleteTicket(ticketId) {
      // Prevent deletion if worked time exists
      if (this.ticket.logged_time > 0) {
        this.toast.error(`Cannot delete. This ticket has ${this.ticket.logged_time} hours of worked time.`);
        return;
      }

      const tmp = confirm(`Are you sure you would like to delete ticket #${this.ticket.id}?`);
      if (!tmp) {
        return;
      }

      this.$updateLoading(1);
      try {
        const response = await axios.post('ticket/summary-delete', { delete: ticketId });
        this.toast.success(response.data.message);
        this.$router.push('/viewTickets'); // Redirect to tickets list
      } catch (error) {
        console.error('Error deleting ticket:', error);
        this.toast.error(error.response.data.message || 'Error deleting ticket');
      } finally {
        this.$updateLoading(-1);
      }
    },
  }
};
</script>

<style scoped>
.details-transition-enter-active,
.details-transition-leave-active {
  transition: all 0.1s ease;
}

.details-transition-enter,
.details-transition-leave-to

/* .details-transition-leave-active in <2.1.8 */
  {
  opacity: 0;
  transform: translateY(-10px);
}

.quicknote-transition-enter-active,
.quicknote-transition-leave-active {
  transition: all 0.1s ease;
}

.quicknote-transition-enter,
.quicknote-transition-leave-to

/* .quicknote-transition-leave-active in <2.1.8 */
  {
  opacity: 0;
  transform: translateY(-10px);
}

.rotate-180 {
  transform: rotate(180deg);
}

.transition-transform {
  transition: transform 0.1s ease;
}

table td {
  vertical-align: top !important;
  padding-top: 20px;
}

:deep(.markdown-body a) {
  color: #4485d4;
  text-decoration: underline;
}

.dark :deep(.markdown-body a) {
  color: #60a5fa;
}
</style>